import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import MultilingualSitemapGenerator from '@/components/seo/MultilingualSitemapGenerator';
import { generateMultilingualRoutes, getRouteStatistics, generateRobotsTxt, generateSitemapIndex } from '@/utils/multilingualRouteGenerator';
import { cityLanguageNames, cityLanguageFlags } from '@/context/languages/languageData';
import { worldCities } from '@/data/cities';
import { specialties } from '@/data/specialties';
import { Download, Globe, BarChart3, Settings, FileText, Search, Languages, MapPin } from 'lucide-react';

const MultilingualSEOManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const stats = getRouteStatistics();
  const routes = generateMultilingualRoutes();

  const downloadFile = (content: string, filename: string, type: string = 'application/xml') => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const TopLanguagesByUrls = () => {
    const sortedLanguages = Object.entries(stats.byLanguage)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 10);

    return (
      <div className="space-y-3">
        {sortedLanguages.map(([lang, count], index) => (
          <div key={lang} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
            <div className="flex items-center gap-3">
              <span className="text-lg">{cityLanguageFlags[lang as keyof typeof cityLanguageFlags]}</span>
              <div>
                <div className="font-medium">{cityLanguageNames[lang as keyof typeof cityLanguageNames]}</div>
                <div className="text-sm text-muted-foreground uppercase">{lang}</div>
              </div>
            </div>
            <Badge variant="secondary" className="font-mono">
              {count.toLocaleString()} URLs
            </Badge>
          </div>
        ))}
      </div>
    );
  };

  const RouteTypeBreakdown = () => (
    <div className="grid grid-cols-3 gap-4">
      {Object.entries(stats.byType).map(([type, count]) => (
        <div key={type} className="text-center p-4 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg">
          <div className="text-2xl font-bold text-primary">{count.toLocaleString()}</div>
          <div className="text-sm text-muted-foreground capitalize">{type} Pages</div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-3">
            <Globe className="h-8 w-8 text-primary" />
            Multilingual SEO Manager
          </h1>
          <p className="text-muted-foreground text-lg">
            Comprehensive management for {stats.languages} languages across {stats.total.toLocaleString()} pages
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-primary">{stats.total.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Total URLs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-secondary">{stats.languages}</div>
              <div className="text-sm text-muted-foreground">Languages</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-accent">{stats.cities.toLocaleString()}</div>
              <div className="text-sm text-muted-foreground">Cities</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="text-3xl font-bold text-orange-600">{stats.services}</div>
              <div className="text-sm text-muted-foreground">Services</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="sitemaps" className="flex items-center gap-2">
              <Search className="h-4 w-4" />
              Sitemaps
            </TabsTrigger>
            <TabsTrigger value="languages" className="flex items-center gap-2">
              <Languages className="h-4 w-4" />
              Languages
            </TabsTrigger>
            <TabsTrigger value="tools" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Tools
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Route Type Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <RouteTypeBreakdown />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Top Languages by URL Count</CardTitle>
                </CardHeader>
                <CardContent>
                  <TopLanguagesByUrls />
                </CardContent>
              </Card>
            </div>

            {/* Recent Routes Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Sample Multilingual Routes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {routes.slice(0, 20).map((route, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-muted/10 rounded text-sm">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">
                          {route.language.toUpperCase()}
                        </Badge>
                        <code className="text-xs bg-muted/20 px-2 py-1 rounded">
                          {route.path}
                        </code>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {route.type}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {route.priority}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sitemaps">
            <MultilingualSitemapGenerator />
          </TabsContent>

          <TabsContent value="languages" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Language Coverage Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(cityLanguageNames).map(([code, name]) => {
                    const urlCount = stats.byLanguage[code] || 0;
                    const coverage = (urlCount / stats.total) * 100;
                    
                    return (
                      <div key={code} className="space-y-2 p-4 border rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{cityLanguageFlags[code as keyof typeof cityLanguageFlags]}</span>
                          <div>
                            <div className="font-medium text-sm">{name}</div>
                            <div className="text-xs text-muted-foreground uppercase">{code}</div>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs">
                            <span>{urlCount.toLocaleString()} URLs</span>
                            <span>{coverage.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div 
                              className="bg-primary h-1.5 rounded-full" 
                              style={{ width: `${Math.min(coverage, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tools" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    SEO Files Generator
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <Button 
                      onClick={() => downloadFile(generateRobotsTxt(), 'robots.txt', 'text/plain')}
                      className="w-full flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download robots.txt
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={() => downloadFile(generateSitemapIndex(), 'sitemap-index.xml')}
                      className="w-full flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download Sitemap Index
                    </Button>
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    Generate standard SEO files for search engine optimization and crawling directives.
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Implementation Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="p-3 bg-primary/5 border-l-4 border-primary rounded">
                    <strong>Hreflang Implementation:</strong> All city pages include proper hreflang tags for language alternates.
                  </div>
                  <div className="p-3 bg-secondary/5 border-l-4 border-secondary rounded">
                    <strong>URL Structure:</strong> Local language pages use /{'{lang}'}/city/{'{slug}'} pattern.
                  </div>
                  <div className="p-3 bg-accent/5 border-l-4 border-accent rounded">
                    <strong>Priority System:</strong> Local language pages get higher priority (0.95) than English (0.9).
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MultilingualSEOManager;